import { useCallback } from 'react'
import {
  IActivateAs2CertificateInput,
  ICreateAs2CertificateInput,
  IUpdateAs2CertificateInput,
} from 'types/graphqlTypes'
import { useActivateAs2CertificateMutation } from '../graphql/ActivateAS2Certificate'
import { useCreateAs2CertificateMutation } from '../graphql/CreateAs2Certificate'
import { useUpdateAs2CertificateMutation } from '../graphql/UpdateAs2Certificate'

interface IUseAs2CertificateArgs {
  displayUserFeedback: (message: string, level: 'error' | 'info') => void
}

export const useAs2Certificate = ({ displayUserFeedback }: IUseAs2CertificateArgs) => {
  const [createAs2CertificateMutation] = useCreateAs2CertificateMutation()
  const [activateAs2CertificateMutation] = useActivateAs2CertificateMutation()
  const [updateAs2CertificateMutation] = useUpdateAs2CertificateMutation()

  const createAs2Certificate = useCallback(
    async (input: ICreateAs2CertificateInput) => {
      try {
        const response = await createAs2CertificateMutation({
          variables: {
            input,
          },
          refetchQueries: ['AS2ConnectionConfigCertificates'],
        })
        const { data } = response || {}
        const { createAs2Certificate } = data || {}
        const { errors = [] } = createAs2Certificate || {}

        const filteredErrors = errors.filter(item => !!item.message)

        if (filteredErrors.length > 0) {
          const [firstError] = filteredErrors || []
          const { message } = firstError || {}
          displayUserFeedback(message, 'error')
          return
        }

        displayUserFeedback('AS2 Certificate saved successfully.', 'info')
      } catch (e) {
        displayUserFeedback(e.message, 'error')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createAs2CertificateMutation]
  )

  const activateAs2Certificate = useCallback(
    async (input: IActivateAs2CertificateInput) => {
      try {
        const response = await activateAs2CertificateMutation({
          variables: {
            input,
          },
          refetchQueries: ['AS2ConnectionConfigCertificates'],
        })
        const { data } = response || {}
        const { activateAs2Certificate } = data || {}
        const { errors = [] } = activateAs2Certificate || {}

        const filteredErrors = errors.filter(item => !!item.message)

        if (filteredErrors.length > 0) {
          const [firstError] = filteredErrors || []
          const { message } = firstError || {}
          displayUserFeedback(message, 'error')
          return
        }

        displayUserFeedback('AS2 Certificate activated successfully.', 'info')
      } catch (e) {
        displayUserFeedback(e.message, 'error')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activateAs2CertificateMutation]
  )

  const updateAs2Certificate = useCallback(
    async (input: IUpdateAs2CertificateInput) => {
      try {
        const response = await updateAs2CertificateMutation({
          variables: {
            input,
          },
          refetchQueries: ['AS2ConnectionConfigCertificates'],
        })
        const { data } = response || {}
        const { updateAs2Certificate } = data || {}
        const { errors = [] } = updateAs2Certificate || {}

        const filteredErrors = errors.filter(item => !!item.message)

        if (filteredErrors.length > 0) {
          const [firstError] = filteredErrors || []
          const { message } = firstError || {}
          displayUserFeedback(message, 'error')
          return
        }

        displayUserFeedback('AS2 Certificate saved successfully.', 'info')
      } catch (e) {
        displayUserFeedback(e.message, 'error')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateAs2CertificateMutation]
  )

  return {
    createAs2Certificate,
    activateAs2Certificate,
    updateAs2Certificate,
  }
}
